import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS } from "./constants/languages";
import {
  Box,
  Button,
  HStack,
  InputGroup,
  InputLeftElement,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Flex,
  SimpleGrid,
  VStack, // Added for stacking text in the card
  Icon,
  Spinner,
  Divider,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { BrandAvatar } from "./components/BrandAvatar";
import { SearchIcon, HamburgerIcon } from "@chakra-ui/icons";
import NotificationBell from "./components/NotificationBell";
import { FiInbox, FiChevronDown } from "react-icons/fi";
import {
  FaBalanceScale,
  FaBullhorn,
  FaChartLine,
  FaChartPie,
  FaGraduationCap,
  FaHandshake,
  FaLandmark,
  FaLeaf,
  FaMicrochip,
  FaMountain,
  FaTruck,
  FaGlobeEurope,
  FaGlobeAfrica,
  FaUsers,
  FaGlobe,
  FaLayerGroup,
  FaGlobeAsia,
  FaGlobeAmericas,
} from "react-icons/fa";
import { getThemes } from "./services/themeService";
import { API_BASE, createHeaders } from "./services/apiClient";
import { fetchLeaderInbox } from "./services/leaderInboxService";
import { AnimatePresence, motion } from "framer-motion";
import {
  clearAuthStorage,
  getCachedUserBundle,
  getCurrentUserId,
} from "./services/authService";
import { getThemeKey } from "./dashboard/utils/themesmap";
import { parseThemeIds } from "./dashboard/utils/profileUtils";
import { openExternalProfile } from "./services/openExternalProfile";

// const API_BASE = "https://api.globalwineinstitute.org";
// const DEFAULT_HEADERS = {
//   "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
// };
// const THEMES_OFFICER_USER_ID = 7;

// 1. UPDATED: Define Regions with Scholars and Colors
const REGIONAL_COMMITTEES = [
  {
    name: "Western and Southern Europe",
    scholar: "Prof. Clara Vitis",
    color: "#673ab7", // Deep Purple
  },
  {
    name: "Eastern Europe and Central Asia",
    scholar: "Dr. Sandor Tokaji",
    color: "#00796B", // Dark Cyan/Teal
  },
  {
    name: "North America",
    scholar: "Prof. Eleanor Vine",
    color: "#D32F2F", // Dark Red
  },
  {
    name: "Latin America",
    scholar: "Dr. Ricardo Malbec",
    color: "#FF9800", // Orange
  },
  {
    name: "Africa and the Middle East",
    scholar: "Dr. Layla Syrah",
    color: "#5D4037", // Brown/Sepia
  },
  {
    name: "Asia Pacific",
    scholar: "Prof. Kaito Fuji",
    color: "#3F51B5", // Indigo Blue
  },
];

// Mappa dal nome "grezzo" alla chiave di traduzione i18n
const REGION_LABEL_KEYS = {
  "Western and Southern Europe": "reg_western_eu",
  "Eastern Europe and Central Asia": "reg_eastern_eu",
  "North America": "reg_north_america",
  "Latin America": "reg_latin_america",
  "Africa and the Middle East": "reg_africa_me",
  "Asia Pacific": "reg_asia_pacific",
};

// Icon mapping for regional committees
const ICON_MAP = {
  "Western and Southern Europe": FaGlobeEurope,
  "Eastern Europe and Central Asia": FaGlobeEurope,
  "North America": FaGlobeAmericas,
  "Latin America": FaGlobeAmericas,
  "Africa and the Middle East": FaGlobeAfrica,
  "Asia Pacific": FaGlobeAsia,
};

const THEME_ICON_MAP = {
  market_analysis: FaChartLine,
  marketing_branding: FaBullhorn,
  sensory_psychology: FaUsers,
  sustainability_climate: FaLeaf,
  innovation_tech_digital: FaMicrochip,
  governance_strategy: FaLayerGroup,
  finance_investment: FaChartPie,
  human_capital: FaUsers,
  wine_tourism: FaGlobe,
  landscape_territory: FaMountain,
  history_culture: FaLandmark,
  regulation_policy: FaBalanceScale,
  supply_chain: FaTruck,
  social_sustainability: FaHandshake,
  education_training: FaGraduationCap,
};

const SEARCH_SOURCE_LABEL_KEYS = {
  "auth.users": "top_menu.search_source_people",
  "posts.posts": "top_menu.search_source_posts",
  "articles.documents": "top_menu.search_source_documents",
  "public.publications": "top_menu.search_source_publications",
  "public.videos": "top_menu.search_source_videos",
  "public.wineries": "top_menu.search_source_wineries",
  "public.conferences_gwbi": "top_menu.search_source_events",
};

const MotionMenuList = motion(MenuList);

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Clean text nav link with an underlined active state for the current route.
const NavTextLink = ({ to, children }) => {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Box
      as={Link}
      to={to}
      px={1}
      py={2}
      fontSize="sm"
      fontWeight={active ? "semibold" : "medium"}
      color={active ? "gwbi.700" : "gray.700"}
      borderBottomWidth="2px"
      borderColor={active ? "gwbi.600" : "transparent"}
      _hover={{ color: "gray.900", textDecoration: "none" }}
      transition="color 0.15s ease"
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
};

const TopMenu = ({ onLogout }) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [themes, setThemes] = useState([]);
  const [themesLoaded, setThemesLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchAbortRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchContainerRef = useRef(null);
  const [userThemeIds, setUserThemeIds] = useState([]);

  const refreshFollowsFromStorage = useCallback(() => {
    try {
      const fromLocal = localStorage.getItem("userprofile");
      const fromSession = sessionStorage.getItem("userprofile");
      const parsed = fromLocal
        ? JSON.parse(fromLocal)
        : fromSession
          ? JSON.parse(fromSession)
          : null;
      if (!parsed) return;
      const profileObj = parsed.profile ? parsed.profile : parsed;
      setProfile(profileObj);
      const rawThemes =
        parsed?.profile?.themes ??
        parsed?.user?.themes ??
        profileObj?.themes ??
        [];
      setUserThemeIds(parseThemeIds(rawThemes).map(String));
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // Hydrate from the cached profile, and keep the Region/Theme highlights in
  // sync the instant a follow/unfollow happens — no page remount needed.
  useEffect(() => {
    refreshFollowsFromStorage();
    const handleFollowsChanged = () => refreshFollowsFromStorage();
    window.addEventListener("gwbi:follows-changed", handleFollowsChanged);
    window.addEventListener("storage", handleFollowsChanged);
    return () => {
      window.removeEventListener("gwbi:follows-changed", handleFollowsChanged);
      window.removeEventListener("storage", handleFollowsChanged);
    };
  }, [refreshFollowsFromStorage]);
  useEffect(() => {
    let cancelled = false;
    const loadThemes = async () => {
      try {
        const list = await getThemes();
        if (!cancelled) setThemes(list);
      } catch (e) {
        // fallback: leave themes empty; menu will still render
        console.error("Failed to load themes", e);
      } finally {
        if (!cancelled) setThemesLoaded(true);
      }
    };
    loadThemes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Admins get a shortcut straight back into /administration from any page —
  // without it the only way in is the Admin button on the dashboard sidebar.
  const isAdmin = useMemo(() => {
    const bundle = getCachedUserBundle();
    const roleCandidate =
      bundle?.user?.role ||
      bundle?.profile?.role ||
      bundle?.cached?.user?.role ||
      bundle?.cached?.profile?.role ||
      profile?.role ||
      "";
    return String(roleCandidate).trim().toLowerCase() === "admin";
  }, [profile]);

  // Only assigned Regional Coordinators / Thematic Leaders see the Leader Inbox.
  const [isLeader, setIsLeader] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLeaderInbox({ limit: 1 });
        if (!cancelled) setIsLeader((data?.leader_scopes || []).length > 0);
      } catch {
        if (!cancelled) setIsLeader(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userRegions = useMemo(() => {
    const raw = profile?.regions;
    if (!raw) return [];
    if (Array.isArray(raw) && raw.length > 0) return raw.map((s) => String(s).trim());
    if (typeof raw === "string" && raw.trim()) return raw.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  }, [profile]);

  useEffect(() => {
    (async () => {
      try {
        const userId = getCurrentUserId();
        if (!userId) return;
        const res = await fetch(`${API_BASE}/wineries/profile`, {
          headers: createHeaders({ userId }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const raw = data?.profile?.themes || data?.user?.themes || data?.themes || [];
        setUserThemeIds(parseThemeIds(raw).map(String));
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
        searchAbortRef.current = null;
      }
    };
  }, []);

  const handleLogoutClick = useCallback(() => {
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      try {
        clearAuthStorage();
      } catch (error) {
        console.error("Failed to clear auth storage on logout", error);
      }
    }
    navigate("/", { replace: true });
  }, [onLogout, navigate]);

  useEffect(() => {
    const term = searchTerm.trim();

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }

    if (!term) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError("");
      setSearchOpen(false);
      return;
    }

    setSearchLoading(true);
    setSearchError("");

    searchDebounceRef.current = setTimeout(async () => {
      searchDebounceRef.current = null;
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const headers = createHeaders({ json: false });
        const res = await fetch(
          `${API_BASE}/search?q=${encodeURIComponent(term)}&limit=5`,
          {
            method: "GET",
            headers,
            credentials: "include",
            signal: controller.signal,
          },
        );
        if (!res.ok) {
          throw new Error(
            t("top_menu.search_failed_with_status", { status: res.status }),
          );
        }
        const data = await res.json().catch(() => ({ results: [] }));
        const rawItems = Array.isArray(data?.results) ? data.results : [];
        const normalizedItems = rawItems.map((item) => {
          if (!item || item.source !== "auth.users") return item;
          const extra = item.extra || {};
          const profile_visibility =
            extra.profile_visibility ?? item.profile_visibility ?? "public";
          const allow_access = extra.allow_access ?? item.allow_access;
          return {
            ...item,
            profile_visibility,
            allow_access,
            extra: {
              ...extra,
              profile_visibility,
              allow_access,
            },
          };
        });
        const visibleItems = normalizedItems.filter((item) => {
          if (!item || item.source !== "auth.users") return true;
          const profileVisibility =
            item.profile_visibility ??
            item.extra?.profile_visibility ??
            "public";
          const allowAccess = item.allow_access ?? item.extra?.allow_access;
          return profileVisibility !== "private" && allowAccess !== false;
        });
        const grouped = visibleItems.reduce((acc, item) => {
          const src = item?.source || "other";
          if (!acc[src]) acc[src] = [];
          acc[src].push(item);
          return acc;
        }, {});
        const groups = Object.entries(grouped).map(([source, arr]) => ({
          source,
          label:
            t(
              SEARCH_SOURCE_LABEL_KEYS[source] ||
                "top_menu.search_source_other",
              {
                source,
              },
            ) || source,
          items: arr,
        }));
        groups.sort((a, b) => a.label.localeCompare(b.label));
        setSearchResults(groups);
        setSearchOpen(true);
        setSearchError("");
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Global search failed", error);
        setSearchResults([]);
        setSearchError(error.message || t("top_menu.search_failed_default"));
      } finally {
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null;
        }
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchTerm, t]);

  useEffect(() => {
    const handleClick = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      setSearchOpen(true);
    }
  }, [searchTerm]);

  // useEffect(() => {
  //   // Officer lookup disabled until assignments are confirmed.
  // }, []);

  const displayName =
    `${(profile?.first_name || "").trim()} ${(
      profile?.last_name || ""
    ).trim()}`.trim() || t("top_menu.me");
  const avatarSrc = profile?.avatar_url || undefined;

  const color = "gray.800";
  const menuListBg = "surface.rail";
  const hoverColor = "gray.900";
  const searchResultsBg = "surface.card";
  const searchBorderColor = "border.default";
  const searchHoverBg = "surface.subtle";
  const searchPrimaryColor = "gray.800";
  const searchSecondaryColor = "text.muted";
  const searchEmptyColor = "text.muted";


  const getResultPreview = useCallback((item) => {
    const extra = item?.extra || {};
    switch (item?.source) {
      case "auth.users":
        return (
          extra.avatar_url || extra.photo_url || extra.profile_image_url || null
        );
      case "articles.documents":
      case "public.publications":
      case "public.videos":
      case "public.wineries":
        return extra.thumbnail_url || extra.logo_url || extra.image_url || null;
      default:
        return null;
    }
  }, []);

  const openProfileExternFromSearch = useCallback(
    async (userId) => {
      if (userId == null) return false;
      const normalized = String(userId).trim();
      if (!normalized) return false;
      const headers = createHeaders({ json: true });
      const endpoints = [
        `${API_BASE}/wineries/profile?user_id=${encodeURIComponent(
          normalized,
        )}`,
        `${API_BASE}/profiles?user_id=${encodeURIComponent(normalized)}`,
        `${API_BASE}/users/profile?user_id=${encodeURIComponent(normalized)}`,
      ];
      const buildSnapshot = (data) => {
        if (!data) return null;
        const profilePayload =
          data.profile ||
          data.data?.profile ||
          data.profile_data ||
          data.profileInfo ||
          (data.data && !data.data.profile ? data.data : null) ||
          (!data.profile && data.user ? data.user.profile : null) ||
          data;
        const userPayload = data.user ||
          data.data?.user ||
          profilePayload?.user || {
            id:
              data?.user_id ||
              profilePayload?.user_id ||
              profilePayload?.id ||
              normalized,
            email: profilePayload?.email || data?.email || "",
            username: profilePayload?.username || data?.username || "",
            role: profilePayload?.role || data?.role || "",
          };
        if (!userPayload || userPayload.id == null) {
          userPayload.id = normalized;
        }
        return {
          user: {
            id: userPayload.id,
            email: userPayload.email || "",
            username: userPayload.username || "",
            role: userPayload.role || "",
          },
          profile:
            profilePayload && typeof profilePayload === "object"
              ? profilePayload
              : {},
        };
      };
      for (const url of endpoints) {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) continue;
          const data = await res.json().catch(() => null);
          const snapshot = buildSnapshot(data);
          if (snapshot && snapshot.user?.id) {
            localStorage.setItem("extern_user_id", String(snapshot.user.id));
            localStorage.setItem("extern_profile", JSON.stringify(snapshot));
            await openExternalProfile(navigate, userId);
            return true;
          }
        } catch (error) {
          console.warn("Failed to load external profile from search", error);
        }
      }
      return false;
    },
    [navigate],
  );

  const handleResultClick = useCallback(
    async (item) => {
      if (!item) return;
      const extra = item.extra || {};

      // People → external profile page
      if (item.source === "auth.users") {
        const candidates = [
          item.user_id,
          item.userId,
          item.id,
          extra.user_id,
          extra.userId,
          extra.id,
        ];
        const userId =
          candidates.find(
            (candidate) =>
              candidate != null && String(candidate).trim().length > 0,
          ) || null;
        const opened = await openProfileExternFromSearch(userId);
        if (opened) {
          setSearchOpen(false);
          return;
        }
      }

      // In-app routes by source
      const inAppPath = (() => {
        switch (item.source) {
          case "public.conferences_gwbi":
            return item.id != null ? `/events/${item.id}` : null;
          case "public.wineries": {
            const locationId = extra.location_id || item.id;
            return locationId != null ? `/wineries/${locationId}` : null;
          }
          case "articles.documents":
            return extra.public_url || extra.source_url || null;
          default:
            return null;
        }
      })();

      if (inAppPath) {
        if (/^https?:/i.test(inAppPath)) {
          window.open(inAppPath, "_blank", "noopener,noreferrer");
        } else {
          navigate(inAppPath);
        }
        setSearchOpen(false);
        return;
      }

      // Fallback: any URL the backend sent in extra
      const target =
        extra.public_url ||
        extra.url ||
        extra.link ||
        extra.website ||
        extra.source_url ||
        null;
      if (target) {
        if (/^https?:/i.test(target)) {
          window.open(target, "_blank", "noopener,noreferrer");
        } else {
          navigate(target);
        }
      }
      setSearchOpen(false);
    },
    [openProfileExternFromSearch, navigate],
  );

  return (
    <Box
      as="header"
      bg="surface.card"
      px={3}
      py={2}
      borderBottom="1px solid"
      borderColor="border.default"
      boxShadow="0 1px 3px rgba(0, 0, 0, 0.04)"
    >
      <Flex align="center" width="100%" gap={3}>
        {/* LEFT zone — logo / branding only */}
        <HStack flexShrink={0} pl={1} align="center">
          <Link to="/about">
            <Box
              boxSize={12}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src="/gwbi_logo_red.png"
                alt="logo"
                style={{ width: "100%", height: "100%" }}
              />
            </Box>
          </Link>
        </HStack>

        {/* CENTER zone — global search, the page's focal point */}
        <Flex
          flex="1 1 auto"
          minW={0}
          justify="center"
          display={{ base: "none", md: "flex" }}
        >
        <Box
          position="relative"
          w="100%"
          maxW="460px"
          ref={searchContainerRef}
        >
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="100%"
                py={1}
              >
                <SearchIcon color={color} boxSize={3.5} />
              </InputLeftElement>
              <Input
                type="text"
                placeholder={t("top_menu.search_placeholder")}
                bg="surface.subtle"
                color="gray.800"
                _placeholder={{ color: "text.muted" }}
                size="sm"
                borderRadius="full"
                borderWidth="1px"
                borderColor="border.default"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchTerm.trim()) setSearchOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearchOpen(false);
                    setSearchTerm("");
                  }
                }}
              />
            </InputGroup>
            {searchOpen && (searchLoading || searchTerm.trim()) && (
              <Box
                position="absolute"
                top="calc(100% + 6px)"
                left={0}
                w="100%"
                bg={searchResultsBg}
                borderRadius="md"
                boxShadow="lg"
                borderWidth="1px"
                borderColor={searchBorderColor}
                zIndex={5000}
                maxH="360px"
                overflowY="auto"
              >
                <Box p={3}>
                  {searchLoading ? (
                    <HStack spacing={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color={searchSecondaryColor}>
                        {t("top_menu.searching")}
                      </Text>
                    </HStack>
                  ) : searchError ? (
                    <Text fontSize="sm" color="red.400">
                      {searchError}
                    </Text>
                  ) : searchResults.length === 0 ? (
                    <Text fontSize="sm" color={searchEmptyColor}>
                      {t("top_menu.no_results")}
                    </Text>
                  ) : (
                    searchResults.map((group, groupIdx) => (
                      <Box
                        key={group.source}
                        mb={groupIdx === searchResults.length - 1 ? 0 : 3}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          textTransform="uppercase"
                          color={searchSecondaryColor}
                          mb={2}
                        >
                          {group.label}
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {group.items.map((item) => {
                            const previewUrl = getResultPreview(item);
                            const extra = item.extra || {};
                            const secondary =
                              item.snippet ||
                              extra.organization ||
                              extra.role ||
                              extra.description ||
                              extra.insight ||
                              "";
                            return (
                              <HStack
                                key={`${group.source}-${item.id}`}
                                align="start"
                                spacing={3}
                                p={2}
                                borderRadius="md"
                                _hover={{
                                  bg: searchHoverBg,
                                  cursor: "pointer",
                                }}
                                onClick={() => handleResultClick(item)}
                              >
                                {previewUrl ? (
                                  <Image
                                    src={previewUrl}
                                    alt={item.title || "result"}
                                    boxSize="40px"
                                    objectFit="cover"
                                    borderRadius="md"
                                  />
                                ) : (
                                  <BrandAvatar
                                    size="sm"
                                    name={item.title || group.label}
                                    bg="surface.subtle"
                                    color="text.muted"
                                  />
                                )}
                                <VStack align="start" spacing={1} flex={1}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color={searchPrimaryColor}
                                  >
                                    {item.title || t("top_menu.untitled")}
                                  </Text>
                                  {secondary ? (
                                    <Text
                                      fontSize="xs"
                                      color={searchSecondaryColor}
                                      noOfLines={2}
                                    >
                                      {secondary}
                                    </Text>
                                  ) : null}
                                </VStack>
                              </HStack>
                            );
                          })}
                        </VStack>
                        {groupIdx !== searchResults.length - 1 && (
                          <Divider
                            mt={3}
                            mb={1}
                            borderColor={searchBorderColor}
                          />
                        )}
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Flex>

        {/* RIGHT zone — navigation, utilities, profile */}
        <Box
          flexShrink={0}
          display={{ base: "none", sm: "flex" }}
          justifyContent="flex-end"
          whiteSpace="nowrap"
        >
          <HStack spacing={5} py={1} justify="flex-end" align="center">
            <NavTextLink to="/dashboard">{t("top_menu.home")}</NavTextLink>
            <NavTextLink to="/gwbi">{t("top_menu.gwbi_short")}</NavTextLink>

            <Menu placement="bottom-start" gutter={16} closeOnSelect={true}>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    bg="transparent"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="medium"
                    _hover={{ bg: "transparent", color: "gray.900" }}
                    _active={{ bg: "transparent" }}
                    px={1}
                    py={2}
                    rightIcon={<Icon as={FiChevronDown} boxSize={3} />}
                    aria-label={t("top_menu.aria_regions_menu")}
                  >
                    {t("reg_region_label")}
                  </MenuButton>

                  <AnimatePresence>
                    {isOpen && (
                      <MotionMenuList
                        key="regionsMenu"
                        p={0}
                        minW="fit-content"
                        bg={menuListBg}
                        border="none"
                        boxShadow="2xl"
                        borderRadius="lg"
                        overflow="hidden"
                        zIndex={5000}
                        initial={{ opacity: 0, scale: 0.96, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        transition={{ duration: 0.12, ease: "easeOut" }}
                      >
                        <SimpleGrid
                          columns={3}
                          spacing={3}
                          p={4}
                          minChildWidth="200px"
                        >
                          {REGIONAL_COMMITTEES.map((r) => {
                            const RegionIcon = ICON_MAP[r.name] || FaGlobe;
                            const labelKey = REGION_LABEL_KEYS[r.name];
                            const label = labelKey ? t(labelKey) : r.name;
                            const isFav = userRegions.includes(r.name);

                            return (
                              <MenuItem
                                key={r.name}
                                as={Link}
                                to={`/tool?scope=region&id=${slugify(r.name)}`}
                                bg={isFav ? "gwbi.50" : "surface.card"}
                                color="gray.800"
                                borderWidth={isFav ? "2px" : undefined}
                                borderColor={isFav ? "gwbi.300" : undefined}
                                borderRadius="md"
                                overflow="hidden"
                                boxShadow="md"
                                p={0}
                                _hover={{
                                  bg: "surface.subtle",
                                  transform: "scale(1.02)",
                                }}
                                transition="all 0.2s"
                                display="block"
                              >
                                <Box p={4}>
                                  <HStack spacing={3} align="center">
                                    <Box
                                      bg="surface.subtle"
                                      color="gray.700"
                                      borderRadius="full"
                                      w="28px"
                                      h="28px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <Icon as={RegionIcon} boxSize={4} />
                                    </Box>

                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      color="gray.800"
                                      noOfLines={2}
                                    >
                                      {label}
                                    </Text>
                                  </HStack>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </SimpleGrid>
                      </MotionMenuList>
                    )}
                  </AnimatePresence>
                </>
              )}
            </Menu>

            <Menu placement="bottom-start" closeOnSelect={true}>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    bg="transparent"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="medium"
                    _hover={{ bg: "transparent", color: "gray.900" }}
                    _active={{ bg: "transparent" }}
                    px={1}
                    py={2}
                    rightIcon={<Icon as={FiChevronDown} boxSize={3} />}
                    aria-label={t("top_menu.aria_themes_menu")}
                  >
                    {t("top_menu.themes")}
                  </MenuButton>

                  <AnimatePresence>
                    {isOpen && (
                      <MotionMenuList
                        key="themesMenu"
                        p={0}
                        minW="fit-content"
                        bg={menuListBg}
                        border="none"
                        boxShadow="2xl"
                        borderRadius="lg"
                        overflow="hidden"
                        zIndex={5000}
                        initial={{ opacity: 0, scale: 0.96, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        transition={{ duration: 0.12, ease: "easeOut" }}
                      >
                        {!themesLoaded ? (
                          <Box px={6} py={4}>
                            <Text fontSize="sm" color={searchSecondaryColor}>
                              {t("top_menu.loading_themes")}
                            </Text>
                          </Box>
                        ) : themes.length === 0 ? (
                          <Box px={6} py={4}>
                            <Text fontSize="sm" color={searchSecondaryColor}>
                              {t("top_menu.no_themes")}
                            </Text>
                          </Box>
                        ) : (
                          <SimpleGrid
                            spacing={3}
                            p={4}
                            templateColumns={{
                              base: "1fr",
                              md: "repeat(3, minmax(0, 220px))",
                            }}
                            justifyContent="center"
                            justifyItems="stretch"
                          >
                            {themes.map((theme) => {
                              const themeKey = getThemeKey(theme);
                              const label = t(`themes.${themeKey}`);
                              const slug = slugify(themeKey);
                              const themeLink = `/tool?scope=theme&id=${slug}`;
                              const ThemeIcon = THEME_ICON_MAP[themeKey] || FaLayerGroup;
                              const isFav = userThemeIds.includes(String(theme.id));

                              return (
                                <MenuItem
                                  key={theme.id || themeKey}
                                  as={Link}
                                  to={themeLink}
                                  bg={isFav ? "gwbi.50" : "surface.card"}
                                  color="gray.800"
                                  borderWidth={isFav ? "2px" : undefined}
                                  borderColor={isFav ? "gwbi.300" : undefined}
                                  borderRadius="md"
                                  overflow="hidden"
                                  boxShadow="md"
                                  p={0}
                                  _hover={{
                                    bg: "surface.subtle",
                                    transform: "scale(1.02)",
                                  }}
                                  transition="all 0.2s"
                                  display="block"
                                >
                                  <Box p={4}>
                                    <HStack spacing={3} align="center">
                                      <Box
                                        bg="surface.subtle"
                                        color="gwbi.600"
                                        borderRadius="full"
                                        w="28px"
                                        h="28px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexShrink={0}
                                      >
                                        <Icon as={ThemeIcon} boxSize={3.5} />
                                      </Box>
                                      <Text
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        color="gray.800"
                                        whiteSpace="normal"
                                      >
                                        {label}
                                      </Text>
                                    </HStack>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </SimpleGrid>
                        )}
                      </MotionMenuList>
                    )}
                  </AnimatePresence>
                </>
              )}
            </Menu>

            <NavTextLink to="/my">
              {t("top_menu.my_world") || "My World"}
            </NavTextLink>

            <NavTextLink to="/cardview">{t("top_menu.grid")}</NavTextLink>

            {/* Utilities: messages, notifications, profile */}
            <Box
              px={1}
              py={1}
              display="inline-flex"
              color={color}
            >
              <HStack spacing={6} align="center">
                {isLeader && (
                  <IconButton
                    as={Link}
                    to="/inbox"
                    aria-label="Leader Inbox"
                    icon={<FiInbox />}
                    variant="ghost"
                    size="md"
                  />
                )}
                <Menu placement="bottom-end" closeOnSelect>
                  <MenuButton
                    as={IconButton}
                    aria-label={t("top_menu.language", "Language")}
                    icon={<Icon as={FaGlobe} boxSize={5} />}
                    variant="ghost"
                    size="md"
                    color={color}
                    _hover={{ bg: "transparent", color: hoverColor }}
                    _active={{ bg: "transparent" }}
                  />
                  <MenuList
                    bg={menuListBg}
                    border="none"
                    boxShadow="2xl"
                    borderRadius="lg"
                    overflow="hidden"
                    minW="170px"
                    zIndex={5000}
                  >
                    {LANGUAGE_OPTIONS.map(({ code, label }) => {
                      const active =
                        i18n.language === code ||
                        (i18n.language || "").split("-")[0] === code;
                      return (
                        <MenuItem
                          key={code}
                          onClick={() => i18n.changeLanguage(code)}
                          bg="transparent"
                          color={active ? "gwbi.600" : color}
                          fontWeight={active ? "semibold" : "normal"}
                          _hover={{ bg: searchHoverBg, color: active ? "gwbi.600" : color }}
                          px={3}
                          py={2}
                        >
                          <HStack justify="space-between" w="100%">
                            <Text fontSize="sm">{label}</Text>
                            <Text fontSize="xs" color="text.muted">
                              {code.toUpperCase()}
                            </Text>
                          </HStack>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
                <NotificationBell />
                <Menu>
                  <MenuButton>
                    <BrandAvatar size="sm" name={displayName} src={avatarSrc} />
                  </MenuButton>
                  <MenuList
                    bg={menuListBg}
                    color={color}
                    border="none"
                    boxShadow="2xl"
                    borderRadius="lg"
                    overflow="hidden"
                    zIndex={5000}
                  >
                    <MenuItem
                      as={Link}
                      to="/profileextern"
                      bg="transparent"
                      _hover={{ bg: searchHoverBg, color }}
                      px={3}
                      py={2}
                    >
                      {t("top_menu.profile")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => navigate("/settings")}
                      bg="transparent"
                      _hover={{ bg: searchHoverBg, color }}
                      px={3}
                      py={2}
                    >
                      {t("top_menu.settings")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => navigate("/invite")}
                      bg="transparent"
                      _hover={{ bg: searchHoverBg, color }}
                      px={3}
                      py={2}
                    >
                      {t("top_menu.invite", "Invite a colleague")}
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem
                        as={Link}
                        to="/administration"
                        bg="transparent"
                        _hover={{ bg: searchHoverBg, color }}
                        px={3}
                        py={2}
                      >
                        {t("top_menu.admin", "Admin")}
                      </MenuItem>
                    )}
                    <Divider my={1} borderColor="border.default" />
                    <MenuItem
                      onClick={handleLogoutClick}
                      bg="transparent"
                      _hover={{ bg: searchHoverBg, color }}
                      px={3}
                      py={2}
                    >
                      {t("top_menu.logout")}
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Box>
          </HStack>
        </Box>
        {/* Mobile hamburger menu (shown when full menu is hidden) */}
        <Box
          flex="1"
          display={{ base: "flex", sm: "none" }}
          justifyContent="flex-end"
          pr={2}
        >
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<HamburgerIcon />}
              variant="ghost"
              color={color}
              _hover={{ bg: "transparent", color: hoverColor }}
              _active={{ bg: "transparent" }}
              aria-label={t("top_menu.aria_mobile_menu")}
              size="sm"
            />
            <MenuList
              bg={menuListBg}
              color={color}
              border="none"
              boxShadow="2xl"
            >
              <MenuItem as={Link} to="/dashboard">
                {t("top_menu.home")}
              </MenuItem>
              <MenuItem as={Link} to="/about">
                {t("top_menu.gwbi_about")}
              </MenuItem>
              <MenuItem as={Link} to="/gwbi/people">
                {t("top_menu.gwbi_people") || "People"}
              </MenuItem>
              <MenuItem as={Link} to="/my">
                {t("top_menu.my_world") || "My World"}
              </MenuItem>
              <MenuItem as={Link} to="/cardview">
                {t("top_menu.grid")}
              </MenuItem>
              <MenuItem as={Link} to="/profileextern">
                {t("top_menu.profile")}
              </MenuItem>
              <MenuItem as={Link} to="/invite">
                {t("top_menu.invite", "Invite a colleague")}
              </MenuItem>
              {isAdmin && (
                <MenuItem as={Link} to="/administration">
                  {t("top_menu.admin", "Admin")}
                </MenuItem>
              )}
              <MenuItem onClick={handleLogoutClick}>
                {t("top_menu.logout")}
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Flex>
    </Box>
  );
};

export default TopMenu;
